import { ErrorBoundary, initMonitoring } from '@kostbar/monitoring'

import { StrictMode } from 'react'
import ReactDOM from 'react-dom/client'
import {
  Outlet,
  RouterProvider,
  createRootRoute,
  createRoute,
  createRouter,
} from '@tanstack/react-router'
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools'
import { Toaster } from 'sonner'
import { useTranslation } from '@kostbar/i18n'
import * as TanStackQueryProvider from './integrations/tanstack-query/root-provider.tsx'

// Initialize i18n before rendering
import './i18n'

import './styles.css'
import reportWebVitals from './reportWebVitals.ts'

// Auth
import { LoginPage } from './features/auth/pages/login-page'
import { ProtectedRoute } from './features/auth/components/protected-route'
import { PermissionGate } from './shared/components/permission-gate'
import { ACTIONS } from './shared/constants/actions'

// Layout
import { AppShell } from './shared/components/layout/app-shell'

// Pages
import { DashboardPage } from './features/dashboard/pages/dashboard-page'
import { ProfilePage } from './features/profile/pages/profile-page'
import { ProductsListPage } from './features/products/pages/products-list-page'
import { ProductCreatePage } from './features/products/pages/product-create-page'
import { ProductDetailPage } from './features/products/pages/product-detail-page'
import { PricingImportPage } from './features/pricing/pages/pricing-import-page'
import { PricesMatrixPage } from './features/pricing/pages/prices-matrix-page'
import { BundlesPage } from './features/bundles/pages/bundles-page'
import { EdgesPage } from './features/edges/pages/edges-page'
import { CategoriesPage } from './features/categories/pages/categories-page'
import { RestaurantsPage } from './features/restaurants/pages/restaurants-page'
import { RestaurantEdgeSettingsPage } from './features/restaurants/pages/restaurant-edge-settings-page'
import { RestaurantSchedulingPage } from './features/restaurants/pages/restaurant-scheduling-page'
import { RestaurantVoidReasonsPage } from './features/restaurants/pages/restaurant-void-reasons-page'
import { RestaurantEntnahmeReasonsPage } from './features/restaurants/pages/restaurant-entnahme-reasons-page'
import { RestaurantEslPage } from './features/restaurants/pages/restaurant-esl-page'
import { RestaurantPaymentsPage } from './features/restaurants/pages/restaurant-payments-page'
import { RestaurantKioskBrandingPage } from './features/restaurants/pages/restaurant-kiosk-branding-page'
import { RestaurantPurchaseOrderingPage } from './features/restaurants/pages/restaurant-purchase-ordering-page'
import { MenusListPage } from './features/menus/pages/menus-list-page'
import { MenuEditorPage } from './features/menus/pages/menu-editor-page'
import { InventoryPage } from './features/inventory/pages/inventory-page'
import { InventoryHistoryPage } from './features/inventory/pages/inventory-history-page'
import { DeliveryListPage } from './features/inventory-deliveries/pages/delivery-list-page'
import { DeliveryFormPage } from './features/inventory-deliveries/pages/delivery-form-page'
import { DeliveryDetailPage } from './features/inventory-deliveries/pages/delivery-detail-page'
import { PurchaseOrderListPage } from './features/purchase-orders/pages/purchase-order-list-page'
import { PurchaseOrderDetailPage } from './features/purchase-orders/pages/purchase-order-detail-page'
import { CountSessionHistoryPage } from './features/inventory-count/pages/count-session-history-page'
import { CountSessionDetailPage } from './features/inventory-count/pages/count-session-detail-page'
import { CountMobileLayout } from './features/inventory-count/components/count-mobile-layout'
import { CountCapturePage } from './features/inventory-count/pages/count-capture-page'
import { CountReviewPage } from './features/inventory-count/pages/count-review-page'
import { TransactionsPage } from './features/transactions/pages/transactions-page'
import { PosLayoutPage } from './features/pos-layout/pages/pos-layout-page'
import { StaffPage } from './features/staff/pages/staff-page'
import { VouchersPage } from './features/vouchers/pages/vouchers-page'
import { UsersPage } from './features/users/pages/users-page'
import { UserCreatePage } from './features/users/pages/user-create-page'
import { UserDetailPage } from './features/users/pages/user-detail-page'
import { CustomersPage } from './features/customers/pages/customers-page'
import { RolesPage } from './features/roles/pages/roles-page'
import { WebbillSyncPage } from './features/webbill-sync/pages/webbill-sync-page'
import { IntegrationsPage } from './features/integrations/pages/integrations-page'
import { SalesReportPage } from './features/reports/views/sales/sales-report-page'
import { PickupReportPage } from './features/reports/views/pickups/pickup-report-page'
import { SalesBreakdownPage } from './features/reports/views/sales/sales-breakdown-page'
import { ArticlesPage } from './features/reports/views/articles/articles-page'
import { OrderProposalPage } from './features/reports/views/order-proposal/order-proposal-page'
import { AccountingPage } from './features/reports/views/accounting/accounting-page'
import { parseAccountingSearch } from './features/reports/lib/accounting-period'
import type {
  ReportDimension,
  ReportMeasure,
} from './features/reports/api/use-sales-report-query'
import type { PeriodGrain, PeriodPreset } from './features/reports/lib/period'

/** Kept beside the routes: a search param has to be validated against the same list the UI offers. */
const REPORT_DIMENSIONS: Array<ReportDimension> = [
  'time_week',
  'time_day',
  'time_hour',
  'weekday_hour',
  'daypart',
  'channel',
  'category_main',
  'category_sub',
  'article',
  'payment_method',
  'restaurant',
]

const PERIOD_PRESETS: Array<PeriodPreset> = [
  'today',
  '7d',
  '14d',
  '30d',
  'custom',
]

// Initialize monitoring before any other code runs
initMonitoring({
  app: 'backoffice-app',
  environment: import.meta.env.MODE,
  enabled: import.meta.env.PROD,
})

// Root route - no layout, just outlet
const rootRoute = createRootRoute({
  component: () => (
    <>
      <Outlet />
      <Toaster />
      <TanStackRouterDevtools />
    </>
  ),
})

// Public login route
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
})

// Protected layout route
const layoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'layout',
  component: () => (
    <ProtectedRoute>
      <AppShell />
    </ProtectedRoute>
  ),
})

// Protected dashboard (index)
//
// `?period=today` is a state of this page, not a second page (CHG-2026-09-manager-reporting): the
// trend becomes hourly, the fourth tile becomes a forecast, and every comparison switches to the
// same weekday up to the same time of day.
const indexRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/',
  validateSearch: (
    search: Record<string, unknown>,
  ): { period?: PeriodPreset } => {
    const raw = search.period
    return typeof raw === 'string' &&
      PERIOD_PRESETS.includes(raw as PeriodPreset)
      ? { period: raw as PeriodPreset }
      : {}
  },
  component: () => {
    const { period } = indexRoute.useSearch()
    return <DashboardPage period={period ?? 'today'} />
  },
})

// Protected profile route
const profileRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/profile',
  component: ProfilePage,
})

// Protected products routes
const productsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/products',
  component: ProductsListPage,
})

const productCreateRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/products/new',
  component: ProductCreatePage,
})

const productDetailRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/products/$sku',
  component: ProductDetailPage,
  // `tab=stockUsage` opens the "Lagerverbrauch" tab directly — the POS layout editor's stock
  // breakdown links there (CHG-2026-09-stock-usage-and-price-twins, task 11.5).
  validateSearch: (search: Record<string, unknown>): { tab?: 'stockUsage' } =>
    search.tab === 'stockUsage' ? { tab: 'stockUsage' } : {},
})

// Pricing route
const pricingImportRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/pricing/import',
  component: PricingImportPage,
})

const pricingMatrixRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/pricing',
  component: PricesMatrixPage,
})

// Bundles route
const bundlesRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/bundles',
  component: BundlesPage,
})

// Edges route
const edgesRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/edges',
  component: EdgesPage,
})

// Categories route
const categoriesRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/categories',
  component: CategoriesPage,
})

// Restaurants route
const restaurantsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/restaurants',
  component: RestaurantsPage,
})

const restaurantEdgeSettingsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/restaurants/$id/edge-settings',
  component: RestaurantEdgeSettingsPage,
})

const restaurantSchedulingRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/restaurants/$id/scheduling',
  component: RestaurantSchedulingPage,
})

const restaurantVoidReasonsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/restaurants/$id/void-reasons',
  component: RestaurantVoidReasonsPage,
})

const restaurantEntnahmeReasonsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/restaurants/$id/entnahme-reasons',
  component: RestaurantEntnahmeReasonsPage,
})

const restaurantEslRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/restaurants/$id/esl-config',
  component: RestaurantEslPage,
})

const restaurantPaymentsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/restaurants/$id/payments',
  component: RestaurantPaymentsPage,
})

const restaurantKioskBrandingRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/restaurants/$id/kiosk-branding',
  component: RestaurantKioskBrandingPage,
})

const restaurantPurchaseOrderingRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/restaurants/$id/purchase-ordering',
  component: RestaurantPurchaseOrderingPage,
})

// Menus list route
const menusListRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/menus',
  component: MenusListPage,
})

// Menu editor route
const menuEditorRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/menus/$menuId',
  component: MenuEditorPage,
})

const inventoryRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/inventory',
  component: InventoryPage,
})

const inventoryHistoryRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/inventory/history',
  component: InventoryHistoryPage,
  // `reference` scopes the timeline to one document (currently `delivery:<id>`), which is how a
  // booked delivery links back into the history.
  validateSearch: (
    search: Record<string, unknown>,
  ): { reference?: string } => ({
    reference:
      typeof search.reference === 'string' && search.reference !== ''
        ? search.reference
        : undefined,
  }),
})

// Announced deliveries (Wareneingang): list, announce/edit form, and the detail page that
// doubles as the check-and-book view while the document is ANNOUNCED.
const deliveryListRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/inventory/deliveries',
  component: DeliveryListPage,
})

const deliveryNewRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/inventory/deliveries/new',
  component: DeliveryFormPage,
})

const deliveryDetailRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/inventory/deliveries/$id',
  component: DeliveryDetailPage,
  // Deep links (inventory history) carry the restaurant, because the delivery endpoints are
  // restaurant-scoped and the persisted selection may point elsewhere.
  validateSearch: (
    search: Record<string, unknown>,
  ): { restaurantId?: number } => {
    const raw = Number(search.restaurantId)
    return Number.isInteger(raw) && raw > 0 ? { restaurantId: raw } : {}
  },
})

const deliveryEditRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/inventory/deliveries/$id/edit',
  component: DeliveryFormPage,
})

// Purchase orders to the wholesaler (CHG-2026-10-edi-purchase-orders): list and one order, which
// is the draft form while DRAFT and read-only once sent.
const purchaseOrderListRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/inventory/purchase-orders',
  component: PurchaseOrderListPage,
})

const purchaseOrderDetailRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/inventory/purchase-orders/$id',
  component: PurchaseOrderDetailPage,
  // The Bestellvorschlag names the Standort of the draft it just created.
  validateSearch: (
    search: Record<string, unknown>,
  ): { restaurantId?: number } => {
    const raw = Number(search.restaurantId)
    return Number.isInteger(raw) && raw > 0 ? { restaurantId: raw } : {}
  },
})

// Inventur: its own top-level section, separate from Inventar stock management.
const countSessionHistoryRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/inventur',
  component: CountSessionHistoryPage,
})

const countSessionDetailRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/inventur/$id',
  component: CountSessionDetailPage,
})

// Counting flow: its own minimal mobile layout (no desktop sidebar), still behind ProtectedRoute.
const countLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'count-layout',
  component: () => (
    <ProtectedRoute>
      <CountMobileLayout />
    </ProtectedRoute>
  ),
})

/*
 * Running a count is gated in the UI, not only in the backend: both screens mutate a session on
 * every interaction, and without the permission each of them would render its camera, sheets and
 * queries only to have every request refused. The gate sits on the route rather than inside the
 * page so those hooks never run at all — when the permission is there it renders nothing of its
 * own.
 */
const countCaptureRoute = createRoute({
  getParentRoute: () => countLayoutRoute,
  path: '/inventory/count/capture',
  component: () => (
    <PermissionGate action={ACTIONS.INVENTORY_COUNT_SESSION}>
      <CountCapturePage />
    </PermissionGate>
  ),
})

const countReviewRoute = createRoute({
  getParentRoute: () => countLayoutRoute,
  path: '/inventory/count/review',
  component: () => (
    <PermissionGate action={ACTIONS.INVENTORY_COUNT_SESSION}>
      <CountReviewPage />
    </PermissionGate>
  ),
})

const transactionsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/transactions',
  component: TransactionsPage,
})

const posLayoutRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/pos/layout',
  component: PosLayoutPage,
})

const staffRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/pos/staff',
  component: StaffPage,
})

const vouchersRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/pos/vouchers',
  component: VouchersPage,
})

const usersRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/users',
  component: UsersPage,
})

const userCreateRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/users/new',
  component: UserCreatePage,
})

const userDetailRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/users/$id',
  component: UserDetailPage,
})

const customersRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/customers',
  component: CustomersPage,
})

const rolesRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/roles',
  component: RolesPage,
})

// Sales report route
const salesRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/sales',
  component: SalesReportPage,
})

// Pickup report route (Abholungen)
const pickupsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/pickups',
  component: PickupReportPage,
})

// The one manager report surface (CHG-2026-09-manager-reporting). Five dimensions, one page: the
// search params exist so a dashboard tile can hand its own breakdown and scope over.
const reportsSalesRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/reports/sales',
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    dimension?: ReportDimension
    measure?: ReportMeasure
    period?: PeriodPreset
    from?: string
    to?: string
    fromTime?: string
    toTime?: string
    grain?: PeriodGrain
    categoryId?: number
    sku?: string
  } => {
    const dimension = search.dimension
    const measure = search.measure
    const period = search.period
    const categoryId = Number(search.categoryId)
    // `YYYY-MM-DD` only — anything else is dropped rather than passed to the backend, which would
    // answer a query nobody asked for.
    const isDay = (v: unknown): v is string =>
      typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)
    // `HH:mm` only, and one of the four grains — anything else is dropped rather than handed to
    // the backend, which would answer a question nobody asked.
    const isTime = (v: unknown): v is string =>
      typeof v === 'string' && /^([01]\d|2[0-4]):[0-5]\d$/.test(v)
    const grain = search.grain
    return {
      from: isDay(search.from) ? search.from : undefined,
      to: isDay(search.to) ? search.to : undefined,
      fromTime: isTime(search.fromTime) ? search.fromTime : undefined,
      toTime: isTime(search.toTime) ? search.toTime : undefined,
      grain:
        grain === 'day' ||
        grain === 'week' ||
        grain === 'month' ||
        grain === 'quarter'
          ? grain
          : undefined,
      dimension:
        typeof dimension === 'string' &&
        REPORT_DIMENSIONS.includes(dimension as ReportDimension)
          ? (dimension as ReportDimension)
          : undefined,
      measure:
        measure === 'units' || measure === 'revenue' ? measure : undefined,
      period:
        typeof period === 'string' &&
        PERIOD_PRESETS.includes(period as PeriodPreset)
          ? (period as PeriodPreset)
          : undefined,
      categoryId:
        Number.isInteger(categoryId) && categoryId > 0 ? categoryId : undefined,
      // A comma-separated SKU list, so a report narrowed to a few articles is a link somebody can
      // send. Kept as the raw string and split by the page: the shape a SKU may take is the
      // catalogue's business, not the router's, and anything empty is dropped.
      sku:
        typeof search.sku === 'string' && search.sku.trim() !== ''
          ? search.sku
          : undefined,
    }
  },
  component: () => {
    const search = reportsSalesRoute.useSearch()
    return <SalesBreakdownPage search={search} />
  },
})

const reportsArticlesRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/reports/articles',
  component: ArticlesPage,
})

// The Bestellvorschlag (CHG-2026-10-bestellvorschlag): the page gates itself on the view action.
const reportsOrderProposalRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/reports/order-proposal',
  component: OrderProposalPage,
})

// The controller's filing report (CHG-2026-09-tax-agency-report). Period and Ansicht are search
// params (CHG-2026-10-accounting-close-periods, D7): a close, a month or a run of closes, so the
// back button, a shared link and clicking a close in a list all work; the Standort stays a choice
// made on the page.
const reportsAccountingRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/reports/accounting',
  validateSearch: parseAccountingSearch,
  component: () => {
    const search = reportsAccountingRoute.useSearch()
    const navigate = reportsAccountingRoute.useNavigate()
    return (
      <AccountingPage
        search={search}
        onNavigate={(next) => void navigate({ search: next })}
      />
    )
  },
})

// Webbill Sync route
const webbillSyncRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/webbill-sync',
  component: WebbillSyncPage,
})

// Integrationen: API clients for machine callers (CHG-2026-09-webbill-delivery-import)
const integrationsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/integrations',
  component: () => (
    <PermissionGate action={ACTIONS.INTEGRATIONS_MANAGE}>
      <IntegrationsPage />
    </PermissionGate>
  ),
})

const routeTree = rootRoute.addChildren([
  loginRoute,
  layoutRoute.addChildren([
    indexRoute,
    profileRoute,
    productsRoute,
    productCreateRoute,
    productDetailRoute,
    pricingMatrixRoute,
    pricingImportRoute,
    bundlesRoute,
    edgesRoute,
    categoriesRoute,
    restaurantsRoute,
    restaurantSchedulingRoute,
    restaurantVoidReasonsRoute,
    restaurantEntnahmeReasonsRoute,
    restaurantEdgeSettingsRoute,
    restaurantEslRoute,
    restaurantPaymentsRoute,
    restaurantKioskBrandingRoute,
    restaurantPurchaseOrderingRoute,
    posLayoutRoute,
    staffRoute,
    vouchersRoute,
    usersRoute,
    userCreateRoute,
    userDetailRoute,
    customersRoute,
    rolesRoute,
    menusListRoute,
    menuEditorRoute,
    inventoryRoute,
    inventoryHistoryRoute,
    deliveryListRoute,
    deliveryNewRoute,
    deliveryDetailRoute,
    deliveryEditRoute,
    purchaseOrderListRoute,
    purchaseOrderDetailRoute,
    countSessionHistoryRoute,
    countSessionDetailRoute,
    transactionsRoute,
    salesRoute,
    pickupsRoute,
    reportsSalesRoute,
    reportsArticlesRoute,
    reportsOrderProposalRoute,
    reportsAccountingRoute,
    webbillSyncRoute,
    integrationsRoute,
  ]),
  countLayoutRoute.addChildren([countCaptureRoute, countReviewRoute]),
])

const TanStackQueryProviderContext = TanStackQueryProvider.getContext()
const router = createRouter({
  routeTree,
  context: {
    ...TanStackQueryProviderContext,
  },
  defaultPreload: 'intent',
  scrollRestoration: true,
  defaultStructuralSharing: true,
  defaultPreloadStaleTime: 0,
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

function ErrorFallback({
  error,
  resetError,
}: {
  error: unknown
  componentStack: string
  eventId: string
  resetError: () => void
}) {
  const { t } = useTranslation('app')
  const errorMessage = error instanceof Error ? error.message : String(error)
  return (
    <div className="flex h-screen w-full flex-col items-center justify-center gap-4 p-8">
      <h1 className="text-2xl font-bold text-destructive">
        {t('error.title')}
      </h1>
      <p className="text-muted-foreground">{t('error.message')}</p>
      {import.meta.env.DEV && (
        <pre className="max-w-full overflow-auto rounded bg-muted p-4 text-sm">
          {errorMessage}
        </pre>
      )}
      <button
        onClick={resetError}
        className="rounded bg-primary px-4 py-2 text-primary-foreground"
      >
        {t('error.reload')}
      </button>
    </div>
  )
}

const rootElement = document.getElementById('app')
if (rootElement && !rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement)
  root.render(
    <StrictMode>
      <ErrorBoundary fallback={ErrorFallback}>
        <TanStackQueryProvider.Provider {...TanStackQueryProviderContext}>
          <RouterProvider router={router} />
        </TanStackQueryProvider.Provider>
      </ErrorBoundary>
    </StrictMode>,
  )
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()
